import {useEffect, useMemo, useRef, useState} from 'react'
import {useGoogleMapsApiLoader} from '../../google-maps-api'
import {useCountry} from '../../hooks'
import useEffectEvent from '../../hooks/use-effect-event'

/**
 * Fetches Google Places (New API) autocomplete suggestions for a text query.
 *
 * Caches the Places library and a session token. Reuses the token while typing and
 * resets it after a selection (call `resetSession`).
 *
 * @param {string} query
 * @param {{minChars?: number, debounceMs?: number}} [config]
 * @returns {{suggestions: any[], resetSession: () => Promise<void>}}
 */
export default function useAutocomplete(query, {minChars = 2, debounceMs = 150} = {}) {
    const loadApi = useGoogleMapsApiLoader()
    const countryCode = useCountry()

    const options = useMemo(
        () => ({
            includedRegionCodes: countryCode ? [countryCode] : undefined,
            includedPrimaryTypes: ['street_address', 'premise', 'subpremise', 'route']
        }),
        [countryCode]
    )

    const timerRef = useRef(null)
    const tokenRef = useRef(null)

    // Holds the current "fetch suggestions" function (initialized/reset per session)
    const getSuggestionRef = useRef(null)

    const [suggestions, setSuggestions] = useState([])

    const resetSession = useEffectEvent(async () => {
        await loadApi()

        const {AutocompleteSessionToken, AutocompleteSuggestion} =
            await window.google.maps.importLibrary('places')

        // one token per session (reused while typing)
        const sessionToken = new AutocompleteSessionToken()
        tokenRef.current = sessionToken

        // store callback in ref so we don't re-import/re-create on every render
        getSuggestionRef.current = (q) =>
            AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input: q,
                sessionToken,
                ...options
            })
    })

    // If options change (e.g. country), recreate the callback/session on next use
    useEffect(() => {
        getSuggestionRef.current = null
        tokenRef.current = null
    }, [options])

    useEffect(() => {
        const q = (query || '').trim()

        if (q.length < minChars) {
            setSuggestions([])
            return
        }

        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(async () => {
            try {
                if (!getSuggestionRef.current) {
                    await resetSession()
                }

                const res = await getSuggestionRef.current(q)
                setSuggestions(res?.suggestions || [])
            } catch {
                setSuggestions([])
            }
        }, debounceMs)

        return () => clearTimeout(timerRef.current)
    }, [query, options, minChars, debounceMs, resetSession])

    return {suggestions, resetSession}
}
